import { ROUTES } from '@/constants/routes';
import { useNavigate } from 'react-router-dom';
import { SVListItemCard } from '../core';
import { BankAccountListItem, BankAccountStatus } from '@/types/bankAccounts';
import { formatCurrency } from '@/utils/currency';
import { generateBankAccountDisplayName } from '@/utils/bankAccounts';
import { useTransactionFormModal } from '@/hooks/stores/useTransactionFormModal';
import { TransactionType } from '@/types/transactionRequests';
import { useOwnerSelectFormModal } from '@/hooks/stores/useOwnerSelectFormModal';

type BankAccountsListProps = {
  accounts: BankAccountListItem[];
};

export default function BankAccountsList({ accounts }: BankAccountsListProps) {
  const navigate = useNavigate();
  const { openTransactionFormModal } = useTransactionFormModal();
  const { openOwnerSelectFormModal } = useOwnerSelectFormModal();

  function handleMenuItemClick(
    account: BankAccountListItem,
    type: TransactionType,
  ) {
    const customerId = account.totalOwners === 1 ? account.primaryOwner.id : '';
    const bankAccountId = account.uuid;

    if (customerId) {
      openTransactionFormModal({ bankAccountId, customerId, type });
    } else {
      openOwnerSelectFormModal({ type, bankAccountId });
    }
  }

  return (
    <>
      {accounts.map(account => {
        const {
          uuid,
          status,
          primaryOwner,
          applicationType,
          accountTypeStr,
          availableBalance,
          accountNumberLast4,
          advisorName,
          totalOwners,
          businessName,
        } = account;
        const { fullName, email } = primaryOwner.profile;
        const header = generateBankAccountDisplayName({
          applicationType,
          businessName,
          fullName,
          email,
          totalOwners,
        });

        return (
          <SVListItemCard
            onClick={() => navigate(`${ROUTES.BANK_ACCOUNTS}/${uuid}`)}
            key={uuid}
            header={header}
            status={status}
            statusType="bankAccount"
            manageMenuItems={
              status === BankAccountStatus.Open
                ? [
                    {
                      label: 'Transactions',
                      onClick: () =>
                        navigate(
                          `${ROUTES.BANK_ACCOUNTS}/${uuid}${ROUTES.TRANSACTIONS}`,
                        ),
                    },
                    {
                      label: 'Scheduled Transactions',
                      onClick: () =>
                        navigate(
                          `${ROUTES.BANK_ACCOUNTS}/${uuid}${ROUTES.SCHEDULED_TRANSACTIONS}`,
                        ),
                    },
                    {
                      label: 'Request a Deposit',
                      onClick: () => handleMenuItemClick(account, 'deposit'),
                    },
                    {
                      label: 'Request a Transfer',
                      onClick: () => handleMenuItemClick(account, 'transfer'),
                    },
                    {
                      label: 'Request a Wire',
                      onClick: () => handleMenuItemClick(account, 'wire'),
                    },
                    {
                      label: 'Statements',
                      onClick: () =>
                        navigate(
                          `${ROUTES.BANK_ACCOUNTS}/${uuid}${ROUTES.STATEMENTS}`,
                        ),
                    },
                    {
                      label: 'Tax Documents',
                      onClick: () =>
                        navigate(
                          `${ROUTES.BANK_ACCOUNTS}/${uuid}${ROUTES.TAX_DOCUMENTS}`,
                        ),
                    },
                  ]
                : [
                    {
                      label: 'Transactions',
                      onClick: () =>
                        navigate(
                          `${ROUTES.BANK_ACCOUNTS}/${uuid}${ROUTES.TRANSACTIONS}`,
                        ),
                    },
                  ]
            }
            additionalInfo={[
              {
                label: 'Account Type',
                text: accountTypeStr,
              },
              {
                label: 'Available Balance',
                text: formatCurrency({ amount: availableBalance.value }),
              },
              {
                label: 'Account Number',
                text: `****${accountNumberLast4}`,
              },
              {
                label: 'Advisor',
                text: advisorName,
              },
            ]}
          />
        );
      })}
    </>
  );
}
